"use client";

import type { VideoContainer, VideoQuality } from "mediatools";
import { useTranslations } from "next-intl";
import { OptionRadioGroup } from "../_components/OptionRadioGroup";
import { VideoFormatRadio } from "./forms";
import type { MediaOperation, OperationOptionsProps } from "./types";
import { runVideoConversion } from "./video";

interface CompressVideoOpts {
  format: VideoContainer;
  maxHeight: string; // "source" | "1080" | "720" | "480"
  quality: VideoQuality;
}

function CompressVideoForm({
  options,
  setOptions,
  disabled,
}: OperationOptionsProps<CompressVideoOpts>) {
  const t = useTranslations("mediaTools.options.compressVideo");
  const resolutions = [
    { value: "source", label: t("resolutions.source") },
    { value: "1080", label: "1080p" },
    { value: "720", label: "720p" },
    { value: "480", label: "480p" },
  ];
  const qualities = [
    { value: "high" as const, label: t("qualities.high.label"), hint: t("qualities.high.hint") },
    { value: "medium" as const, label: t("qualities.medium.label") },
    { value: "low" as const, label: t("qualities.low.label"), hint: t("qualities.low.hint") },
  ];

  return (
    <div className="grid gap-6 sm:grid-cols-3">
      <OptionRadioGroup
        name="resolution"
        label={t("resolutionLabel")}
        value={options.maxHeight}
        onChange={(maxHeight) => setOptions({ ...options, maxHeight })}
        options={resolutions}
        disabled={disabled}
      />
      <OptionRadioGroup
        name="quality"
        label={t("qualityLabel")}
        value={options.quality}
        onChange={(quality) => setOptions({ ...options, quality })}
        options={qualities}
        disabled={disabled}
      />
      <VideoFormatRadio
        value={options.format}
        onChange={(format) => setOptions({ ...options, format })}
        disabled={disabled}
        label={t("formatLabel")}
      />
    </div>
  );
}

export const compressVideoOperation: MediaOperation<CompressVideoOpts> = {
  id: "compress-video",
  group: "video",
  inputKinds: ["video"],
  outputKind: "video",
  showSourcePreview: true,
  defaultOptions: { format: "mp4", maxHeight: "720", quality: "medium" },
  OptionsForm: CompressVideoForm,
  run: (ctx, options) =>
    runVideoConversion(
      ctx,
      {
        format: options.format,
        quality: options.quality,
        ...(options.maxHeight !== "source" ? { maxHeight: Number(options.maxHeight) } : {}),
      },
      "compressed"
    ),
};
